import { useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { markSubscribed, useSparkle } from "@/lib/sparkle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

interface Props {
  sourceType?: string;
  heading?: string;
  sub?: string;
  compact?: boolean;
  interests?: string[];
}

export const NewsletterCTA = ({ sourceType = "newsletter", heading, sub, compact = false, interests = [] }: Props) => {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const sparkle = useSparkle();
  const location = useLocation();

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      const params = new URLSearchParams(location.search);
      const utm: Record<string, string> = {};
      ["utm_source", "utm_medium", "utm_campaign"].forEach((k) => {
        const v = params.get(k);
        if (v) utm[k] = v;
      });
      await api.post("/leads/capture", {
        email, source_type: sourceType, source_page: location.pathname,
        interests, utm: Object.keys(utm).length ? utm : null,
        referrer: document.referrer || null, consent: true,
      });
      setDone(true);
      markSubscribed();
      toast.success("You're in. Watch your inbox.");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div data-testid="newsletter-success" className={compact ? "py-2" : "border border-[#00E5FF]/30 bg-[#00E5FF]/5 rounded-lg p-6"}>
        <p className="text-[#00E5FF] font-mono text-sm">✓ Subscribed. Intelligence incoming.</p>
      </div>
    );
  }

  return (
    <div className={compact ? "" : "border border-white/10 bg-[#121212] rounded-lg p-6 sm:p-8"}>
      {!compact && (
        <>
          <h3 className="font-heading font-bold text-white text-lg mb-1">
            {heading || "The signal, not the noise."}
          </h3>
          <p className="text-gray-400 text-sm mb-4">
            {sub || "Daily crypto + regulation intelligence, straight to your inbox. Free."}
          </p>
        </>
      )}
      <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2">
        <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="you@work.com" data-testid="newsletter-email-input"
          className="bg-[#1A1A1A] border-white/10 text-white placeholder:text-gray-500 focus-visible:ring-[#00E5FF]" />
        <Button type="submit" disabled={busy} data-testid="newsletter-submit-button"
          className={`bg-[#7C3AED] text-white font-bold hover:bg-[#6b2fd6] shrink-0 ${sparkle ? "sparkle-btn" : ""}`}>
          {busy ? "..." : "Subscribe"}
        </Button>
      </form>
    </div>
  );
};
